import { useCallback, useEffect, useMemo, useState } from 'react'
import { GROUP_TYPE } from '../../domain/domainCodes'
import { Link } from 'react-router-dom'
import { Plus, ShieldCheck, Trash2, Users } from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import ExcelUploadFields from '../../components/ExcelUploadFields'
import ExportMenu from '../../components/ExportMenu'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import SegmentedControl from '../../components/SegmentedControl'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import { exportGroupUsers, listGroupUsers } from './groupsService'
import { listGroupOptions, listUsers } from '../users/usersService'
import { strings as S } from './strings'
import './modals.css'

const EXCEL_COLUMNS = [{ name: 'Username', required: true }]

const SOURCE_TABS = [
  { id: 'list', label: S.addUsersModal.tabs.list },
  { id: 'excel', label: S.addUsersModal.tabs.excel },
]

/**
 * One group panel, replacing `#users_modal` + `#add_users_modal`.
 *
 * The legacy chains them: the members modal *closes itself* to open the picker, and
 * submitting the picker returns you to neither — so adding members destroys the view
 * you were adding them to. Here both are tabs of one drawer over the still-visible
 * list, and adding members drops you back on the members tab with the new rows in it.
 *
 * The `removeActions` rule is unchanged: everything that edits membership only shows
 * when the group is not a department (`row._5 !== "1"`, js.cshtml:395-419).
 */
export default function GroupMembersDrawer({ open, group, onClose, onAddUsers, onRemoveUsers }) {
  const [tab, setTab] = useState('members')

  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [query, setQuery] = useState('')

  const [source, setSource] = useState('list')
  const [groupFilter, setGroupFilter] = useState('')
  const [groupOptions, setGroupOptions] = useState([])
  const [candidates, setCandidates] = useState([])
  const [candidatesLoading, setCandidatesLoading] = useState(true)
  const [picked, setPicked] = useState([])
  const [file, setFile] = useState(null)
  const [error, setError] = useState('')
  /** One in-flight action at a time — these all write membership. */
  const [busy, setBusy] = useState(false)

  const editable = group?.typeId !== GROUP_TYPE.DEPARTMENTS

  const loadMembers = useCallback(() => {
    if (!group) return
    setLoading(true)
    listGroupUsers(group.id)
      .then((response) => {
        setMembers(response.rows)
        setLoading(false)
      })
      .catch(() => {
        /* The page toasts the reason; here the table just stops claiming to be loading. */
        setMembers([])
        setLoading(false)
      })
  }, [group])

  useEffect(() => {
    if (!open || !group) return
    setTab('members')
    setSelected([])
    setPicked([])
    setFile(null)
    setError('')
    setQuery('')
    setBusy(false)
    loadMembers()
  }, [open, group, loadMembers])

  useEffect(() => {
    listGroupOptions().then(setGroupOptions)
  }, [])

  useEffect(() => {
    if (!open || tab !== 'add') return
    setCandidatesLoading(true)
    listUsers({
      type: 'ACTIVE',
      groupId: groupFilter === '' ? null : Number(groupFilter),
    })
      .then((response) => {
        setCandidates(response.rows)
        setCandidatesLoading(false)
      })
      .catch(() => {
        setCandidates([])
        setCandidatesLoading(false)
      })
  }, [open, tab, groupFilter])

  const toggle = (setter) => (userName) =>
    setter((previous) =>
      previous.includes(userName)
        ? previous.filter((item) => item !== userName)
        : [...previous, userName],
    )

  const memberColumns = useMemo(
    () => [
      {
        key: 'userName',
        header: S.usersModal.columns.userName,
        sortable: false,
        render: (row) => (
          <Link className="group-modal__link" to={`/users/${row.id}`}>
            <bdi>{row.userName}</bdi>
          </Link>
        ),
      },
      {
        key: 'name',
        header: S.usersModal.columns.name,
        sortable: false,
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'email',
        header: S.usersModal.columns.email,
        sortable: false,
        render: (row) => (
          <span className="group-modal__mail" dir="ltr">
            {row.email}
          </span>
        ),
      },
      {
        key: 'status',
        header: S.usersModal.columns.status,
        sortable: false,
        value: (row) => row.status.map((badge) => badge.label).join(' '),
        render: (row) => (
          <span className="group-modal__chips">
            {row.status.map((badge) => (
              <StatusBadge key={badge.label} tone={badge.color}>
                {badge.label}
              </StatusBadge>
            ))}
          </span>
        ),
      },
    ],
    [],
  )

  const candidateColumns = useMemo(
    () => [
      {
        key: 'userName',
        header: S.addUsersModal.columns.userName,
        render: (row) => <bdi className="group-modal__link">{row.userName}</bdi>,
      },
      { key: 'name', header: S.addUsersModal.columns.nameAr, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'email',
        header: S.addUsersModal.columns.email,
        render: (row) => (
          <span className="group-modal__mail" dir="ltr">
            {row.email}
          </span>
        ),
      },
      {
        key: 'groupNames',
        header: S.addUsersModal.columns.group,
        value: (row) => row.groupNames.join('، '),
        render: (row) => <bdi>{row.groupNames[0] ?? ''}</bdi>,
      },
    ],
    [],
  )

  /**
   * Adding members no longer destroys the members view — it refreshes it.
   *
   * And only once the server has taken the change: a rejected file or a list of names
   * that do not exist leaves the selection where it is, on the tab that made it, so the
   * fix is one edit away rather than a re-pick from scratch.
   */
  const commitAdd = async (payload) => {
    setBusy(true)
    const outcome = await onAddUsers?.(payload)
    setBusy(false)
    if (outcome && !outcome.ok) return

    setPicked([])
    setFile(null)
    setError('')
    setTab('members')
    loadMembers()
  }

  const submitList = () => {
    if (picked.length === 0) return setError(S.addUsersModal.nothingSelected)
    return commitAdd({ users: picked, userGroup: group?.id })
  }

  const submitExcel = () => {
    if (!file) return setError(S.addUsersModal.nothingSelected)
    return commitAdd({ excelFile: file, userGroup: group?.id })
  }

  const submitRemove = async () => {
    setBusy(true)
    const outcome = await onRemoveUsers?.(selected)
    setBusy(false)
    if (outcome && !outcome.ok) return

    setSelected([])
    loadMembers()
  }

  /** `EXCEL` | `PDF` | `DOC` server-side; the menu offers the first two. */
  const runExport = async (format) => {
    setBusy(true)
    const outcome = await exportGroupUsers({ groupId: group?.id, format, search: query })
    setBusy(false)
    if (!outcome.ok) setError(outcome.message)
  }

  const tabs = editable
    ? [
        { id: 'members', label: S.usersModal.title, count: members.length },
        { id: 'add', label: S.usersModal.addUsers },
      ]
    : [{ id: 'members', label: S.usersModal.title, count: members.length }]

  return (
    <Drawer
      dialog
      open={open}
      size="lg"
      title={group?.name ?? S.usersModal.title}
      icon={Users}
      subtitle={group?.typeName}
      onClose={onClose}
      footer={
        tab === 'members' ? (
          <>
            {editable && (
              <Button
                variant="danger"
                disabled={selected.length === 0 || busy}
                onClick={submitRemove}
              >
                <Trash2 size={16} strokeWidth={1.75} />
                {S.usersModal.removeUsers}
              </Button>
            )}
            <ExportMenu
              onExport={runExport}
              labels={{ excel: S.usersModal.exportExcel, pdf: S.usersModal.exportPdf }}
            />
          </>
        ) : (
          <Button disabled={busy} onClick={source === 'list' ? submitList : submitExcel}>
            <Plus size={16} strokeWidth={2} />
            {S.addUsersModal.save}
          </Button>
        )
      }
    >
      <div className="group-drawer">
        <SegmentedControl items={tabs} value={tab} onChange={setTab} ariaLabel={S.usersModal.title} />

        {tab === 'members' && (
          <>
            {editable && (
              <InfoNote icon={ShieldCheck}>
                <bdi>{S.usersModal.hint}</bdi>
              </InfoNote>
            )}

            <FilterBar
              search={{
                value: query,
                onChange: setQuery,
                placeholder: S.usersModal.columns.userName,
              }}
            />

            <DataTable
              /* A list inside a sheet is opened to answer one question; it starts at the top. */
              stateKey={false}
              columns={memberColumns}
              rows={members}
              loading={loading}
              query={query}
              controls={false}
              getRowId={(row) => row.id}
              onRowClick={editable ? (row) => toggle(setSelected)(row.userName) : undefined}
              isRowSelected={editable ? (row) => selected.includes(row.userName) : undefined}
            />
          </>
        )}

        {tab === 'add' && (
          <>
            <SegmentedControl
              items={SOURCE_TABS}
              value={source}
              onChange={setSource}
              ariaLabel={S.addUsersModal.title}
            />

            {source === 'list' && (
              <>
                <InfoNote icon={ShieldCheck}>
                  <bdi>{S.addUsersModal.hint}</bdi>
                </InfoNote>

                <FilterBar
                  chips={
                    groupFilter === ''
                      ? []
                      : [
                          {
                            id: 'group',
                            label: S.addUsersModal.groupPlaceholder,
                            value:
                              groupOptions.find(
                                (option) => String(option.value) === String(groupFilter),
                              )?.label ?? '',
                            onRemove: () => setGroupFilter(''),
                          },
                        ]
                  }
                >
                  <Select
                    value={groupFilter}
                    onChange={setGroupFilter}
                    options={groupOptions}
                    placeholder={S.addUsersModal.groupPlaceholder}
                    aria-label={S.addUsersModal.groupLabel}
                  />
                </FilterBar>

                <DataTable
                  /* A list inside a sheet is opened to answer one question; it starts at the top. */
                  stateKey={false}
                  columns={candidateColumns}
                  rows={candidates}
                  loading={candidatesLoading}
                  controls={false}
                  getRowId={(row) => row.id}
                  onRowClick={(row) => toggle(setPicked)(row.userName)}
                  isRowSelected={(row) => picked.includes(row.userName)}
                />
              </>
            )}

            {source === 'excel' && (
              <ExcelUploadFields
                id="group-add-users-file"
                accept=".xls,.xlsx,.csv"
                columns={EXCEL_COLUMNS}
                templateHref="/root/ExcelTemplates/Groups_Private_Users.xlsx"
                error={error}
                strings={S.excel}
                onChange={(chosen) => {
                  setFile(chosen)
                  setError('')
                }}
              />
            )}

            {error && source === 'list' && (
              <p className="form-row__error" role="alert">
                <bdi>{error}</bdi>
              </p>
            )}
          </>
        )}
      </div>
    </Drawer>
  )
}
